import {
  cloneDeep,
  get,
  find,
  findIndex,
  findLast,
  forEach,
  indexOf,
  isEqual,
  map,
  slice
} from 'lodash';
import turf from '@turf/turf';

import { latlong2featurePoint, getClosestFeaturePointInCollection } from './geojson-utilities';

/**
 * Function which takes a given time and an array of features and sets their properties of
 * distance and time measured from the first one of them using a linear distribution based
 * on their index in the array.
 *
 * @param {object} arg - The arguments object.
 * @param {Array} arg.featureArray - An array with the feature points of the track.
 * @param {number} arg.totalTime - The time to adapt the points to.
 * @returns {Array} An array of feature points with the distance and time measured from
 * the first one of them as their properties.
 */
export const adaptFeatureArrayTimes = ({ featureArray, totalTime }) => {
  let totalDistance = 0;
  let isControlPoint = true;

  const adaptedFeatureArray = map(featureArray, (point, index, array) => {
    if (index > 0) {
      const prevPoint = array[index - 1];
      isControlPoint = index === array.length - 1;
      totalDistance += turf.distance(point, prevPoint);
    }

    const time = Math.floor((index / (array.length - 1)) * totalTime);
    const properties = {
      distanceTravelled: totalDistance,
      isControlPoint,
      timeUS: time
    };
    const coordinates = turf.getCoord(point);

    return turf.point(coordinates, properties);
  });

  return adaptedFeatureArray;
};

/**
 * Function which takes a given time and a feature line string, and returns an array of
 * feature points after setting their properties of distance and time measured from the
 * first point in the line, the attitude and GPS data at each one and if they are control
 * points. The time and distance are calculated using a linear distribution based on their
 * index in the array.
 *
 * @param {object} arg - The arguments object.
 * @param {object} arg.line - A feature line with the coordinates of the track.
 * @param {number} arg.totalTime - The time to adapt the points to.
 * @returns {Array} An array of feature points with the distance and time measured from
 * the first one of them, the attitude and GPS data at each one of them and if they are
 * control points or not as their properties.
 */
export const adaptLineStringTimes = ({ line, totalTime }) => {
  const lineCoordinates = turf.getCoords(line);
  let totalDistance = 0;
  let isControlPoint = true;

  const featureArray = map(lineCoordinates, (point, index, array) => {
    if (index > 0) {
      const prevPoint = array[index - 1];
      isControlPoint = index === array.length - 1;
      totalDistance += turf.distance(point, prevPoint);
    }

    const time = Math.floor((index / (array.length - 1)) * totalTime);
    const properties = {
      distanceTravelled: totalDistance,
      isControlPoint,
      timeUS: time
    };
    let coordinates = [...point];
    if (point.length < 3) coordinates = [...point, 0];

    return turf.point(coordinates, properties);
  });
  return featureArray;
};

/**
 * Function which takes an array of feature points to add intermidiate points between them
 * using a linear distribution. The number of intermidiate points between two points is
 * based on the refresh rate and the difference of time between them.
 *
 * @param {object} arg - The arguments object.
 * @param {Array} arg.featureArray - An array with the coordinates of the track.
 * @param {object} arg.controlPoints - The existing control points.
 * @param {number} arg.refreshRate - The frequency with which the data is updated.
 * @returns {Array} An array of feature points containing both the given and intermidiate
 * points with the distance and time measured from the first of the given points, the
 * attitude and GPS data at each one of them and if they are control points or not as their
 * properties.
 */
export const densifyLine = ({ featureArray, refreshRate }) => {
  const denseLineFeatures = [];

  forEach(featureArray, (point, index, array) => {
    denseLineFeatures.push(point);

    if (index < array.length - 1) {
      const nextPoint = array[index + 1];

      const segmentDistance =
        get(nextPoint, 'properties.distanceTravelled') - get(point, 'properties.distanceTravelled');
      const segmentTime = get(nextPoint, 'properties.timeUS') - get(point, 'properties.timeUS');
      const intermidiatePointsInSegment = Math.floor(segmentTime / (refreshRate * 1000));

      for (let i = 0; i < intermidiatePointsInSegment; i += 1) {
        const bearing = turf.bearing(point, nextPoint);

        const distanceFromFirstPointInSegment =
          ((i + 1) / (intermidiatePointsInSegment + 1)) * segmentDistance;
        const distanceTravelled =
          get(point, 'properties.distanceTravelled') + distanceFromFirstPointInSegment;

        const timeFromFirstPointInSegment =
          ((i + 1) / (intermidiatePointsInSegment + 1)) * segmentTime;
        const time = Math.round(get(point, 'properties.timeUS') + timeFromFirstPointInSegment);

        const options = {
          properties: {
            distanceTravelled,
            isControlPoint: false,
            timeUS: time
          }
        };

        const newIntermidiatePoint = turf.destination(
          point,
          distanceFromFirstPointInSegment,
          bearing,
          options
        );

        denseLineFeatures.push(newIntermidiatePoint);
      }
    }
  });

  return denseLineFeatures;
};

/**
 * Function to reassign a time to all the points in a segment between two control
 * points.
 *
 * @param {object} arg - The arguments object.
 * @param {Array} arg.featureArray - An array of points with the densified coordinates
 * of the track.
 * @param {number} arg.firstIndex - The index of the first control point.
 * @param {number} arg.secondIndex - The index of the second control point.
 * @param {number} arg.firstTime - The time of the first control point.
 * @param {number} arg.secondTime - The time of the second control point.
 * @param {number} arg.firstDistance - The travelled distance of the first control point.
 * @param {number} arg.secondDistance - The travelled distance of the second control point.
 * @returns {object} Another feature collection of points with the
 * reassigned times.
 */
export const syncLineSegment = ({
  featureArray,
  firstIndex,
  secondIndex,
  firstTime,
  secondTime,
  firstDistance,
  secondDistance
}) => {
  const clonedLineFeatures = cloneDeep(featureArray);
  const timeIncrement = secondTime - firstTime;
  const distanceIncrement = secondDistance - firstDistance;

  for (let i = firstIndex + 1; i < secondIndex; i += 1) {
    const distanceFromFirstPoint =
      get(clonedLineFeatures[i], 'properties.distanceTravelled') - firstDistance;
    const timeFromFirstPoint = (distanceFromFirstPoint / distanceIncrement) * timeIncrement;

    const time = Math.round(firstTime + timeFromFirstPoint);
    clonedLineFeatures[i].properties.timeUS = time;
  }

  return clonedLineFeatures;
};

/**
 * Function to assign a time to a control point and sync all the points in the segments
 * between this and the inmediately next and previous control points.
 *
 * @param {object} arg - The arguments object.
 * @param {object[]} arg.clonedFeatureArray - An array of points with the densified coordinates of
 * the track.
 * @param {object} arg.latLngPoint - A feature with the coordinates of the control point.
 * @param {number} arg.videoTime - The time to set the control point at.
 * @returns {object[]} Another array of points with the reassigned times.
 */
export const syncLineAtControlPoint = ({ featureArray, latLngPoint, videoTime }) => {
  const clonedFeatureArray = cloneDeep(featureArray);
  const newControlPoint = getClosestFeaturePointInCollection(
    latlong2featurePoint(latLngPoint),
    turf.featureCollection(clonedFeatureArray)
  );
  newControlPoint.properties.isControlPoint = true;
  newControlPoint.properties.timeUS = videoTime;

  const controlPointIndex = findIndex(clonedFeatureArray, featurePoint =>
    isEqual(featurePoint, newControlPoint)
  );
  const controlPointTime = get(newControlPoint, 'properties.timeUS');
  const controlPointDistance = get(newControlPoint, 'properties.distanceTravelled');

  const prevControlPoint = findLast(
    clonedFeatureArray,
    point =>
      get(point, 'properties.isControlPoint') && get(point, 'properties.timeUS') < controlPointTime
  );
  const prevControlPointIndex = indexOf(clonedFeatureArray, prevControlPoint);
  const prevControlPointTime = get(prevControlPoint, 'properties.timeUS');
  const prevControlPointDistance = get(prevControlPoint, 'properties.distanceTravelled');

  const nextControlPoint = find(
    clonedFeatureArray,
    point =>
      get(point, 'properties.isControlPoint') && get(point, 'properties.timeUS') > controlPointTime
  );
  const nextControlPointIndex = indexOf(clonedFeatureArray, nextControlPoint);
  const nextControlPointTime = get(nextControlPoint, 'properties.timeUS');
  const nextControlPointDistance = get(nextControlPoint, 'properties.distanceTravelled');

  const arrayOfFeaturePointsToSearchForNewControlPoint = slice(
    clonedFeatureArray,
    prevControlPointIndex + 1,
    nextControlPointIndex
  );

  const controlPointCheckedIndex = findIndex(
    arrayOfFeaturePointsToSearchForNewControlPoint,
    featurePoint => isEqual(featurePoint, newControlPoint)
  );

  if (controlPointCheckedIndex < 0) throw new Error('This control point cannot be set there.');

  const firstSegmentUpdatedFeatureArray = syncLineSegment({
    featureArray: clonedFeatureArray,
    firstIndex: prevControlPointIndex,
    secondIndex: controlPointIndex,
    firstTime: prevControlPointTime,
    secondTime: controlPointTime,
    firstDistance: prevControlPointDistance,
    secondDistance: controlPointDistance
  });
  const updatedFeatureArray = syncLineSegment({
    featureArray: firstSegmentUpdatedFeatureArray,
    firstIndex: controlPointIndex,
    secondIndex: nextControlPointIndex,
    firstTime: controlPointTime,
    secondTime: nextControlPointTime,
    firstDistance: controlPointDistance,
    secondDistance: nextControlPointDistance
  });

  return { updatedFeatureArray, newControlPoint };
};

/**
 * Function to remove a control point and resync all the points in the segment between
 * the inmediately next and previous control points.
 *
 * @param {object} arg - The arguments object.
 * @param {object[]} arg.featureArray - An array of points with the densified coordinates of
 * the track.
 * @param {object} arg.selectedControlPoint - The control point to delete.
 * @returns {object[]} Another array of points with the reassigned times.
 */
export const removeControlPoint = ({ featureArray, selectedControlPoint }) => {
  const clonedFeatureArray = cloneDeep(featureArray);
  const controlPoint = find(clonedFeatureArray, selectedControlPoint);
  controlPoint.properties.isControlPoint = false;

  const prevControlPoint = findLast(
    clonedFeatureArray,
    point =>
      get(point, 'properties.isControlPoint') &&
      get(point, 'properties.timeUS') < get(controlPoint, 'properties.timeUS')
  );
  const prevControlPointIndex = indexOf(clonedFeatureArray, prevControlPoint);
  const prevControlPointTime = get(prevControlPoint, 'properties.timeUS');
  const prevControlPointDistance = get(prevControlPoint, 'properties.distanceTravelled');

  const nextControlPoint = find(
    clonedFeatureArray,
    point =>
      get(point, 'properties.isControlPoint') &&
      get(point, 'properties.timeUS') > get(controlPoint, 'properties.timeUS')
  );
  const nextControlPointIndex = indexOf(clonedFeatureArray, nextControlPoint);
  const nextControlPointTime = get(nextControlPoint, 'properties.timeUS');
  const nextControlPointDistance = get(nextControlPoint, 'properties.distanceTravelled');

  const updatedFeatureArray = syncLineSegment({
    featureArray: clonedFeatureArray,
    firstIndex: prevControlPointIndex,
    secondIndex: nextControlPointIndex,
    firstTime: prevControlPointTime,
    secondTime: nextControlPointTime,
    firstDistance: prevControlPointDistance,
    secondDistance: nextControlPointDistance
  });

  return updatedFeatureArray;
};
